import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Mail, Phone, MapPin, Clock } from "lucide-react";

export const Route = createFileRoute("/contacto")({
  head: () => ({
    meta: [
      { title: "Contacto — Bizaclia" },
      {
        name: "description",
        content:
          "Contacta con Bizaclia. Email, teléfono y horario de atención para traspasos, alquileres y venta de locales comerciales.",
      },
      { property: "og:title", content: "Contacto — Bizaclia" },
      {
        property: "og:description",
        content:
          "Email, teléfono y horario de atención de Bizaclia.",
      },
      { property: "og:url", content: "/contacto" },
    ],
    links: [{ rel: "canonical", href: "/contacto" }],
  }),
  component: ContactoPage,
});

function ContactoPage() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Link
        to="/"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al inicio
      </Link>

      <header className="mb-10">
        <h1 className="text-4xl font-bold tracking-tight">Contacto</h1>
        <p className="mt-2 text-muted-foreground">
          Estamos aquí para ayudarte. Escríbenos o llámanos y te responderemos lo antes posible.
        </p>
      </header>

      {/* Contact cards */}
      <section className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-xl border border-border bg-background p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-semibold">General</p>
            </div>
          </div>
          <a
            href="mailto:info@bizaclia.com"
            className="text-lg font-medium text-primary hover:underline"
          >
            info@bizaclia.com
          </a>
          <p className="mt-2 text-sm text-muted-foreground">
            Para consultas generales, soporte y colaboraciones.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-background p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Phone className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Teléfono</p>
              <p className="font-semibold">Atención al cliente</p>
            </div>
          </div>
          <a
            href="tel:+34900433065"
            className="text-lg font-medium text-primary hover:underline"
          >
            +34 900 433 065
          </a>
          <p className="mt-2 text-sm text-muted-foreground">
            Llamada gratuita desde España.
          </p>
        </div>
      </section>

      {/* Info cards */}
      <section className="mb-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-border bg-background p-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-primary" />
            <h3 className="font-semibold">Horario de atención</h3>
          </div>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li className="flex justify-between">
              <span>Lunes – Viernes</span>
              <span className="font-medium text-foreground">09:00 – 18:00</span>
            </li>
            <li className="flex justify-between">
              <span>Sábado</span>
              <span className="font-medium text-foreground">10:00 – 14:00</span>
            </li>
            <li className="flex justify-between">
              <span>Domingo y festivos</span>
              <span className="font-medium text-foreground">Cerrado</span>
            </li>
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-background p-5">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="h-4 w-4 text-primary" />
            <h3 className="font-semibold">Oficina principal</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            Avenida Más Fuster 27, 08197 Sant Cugat del Vallès, Barcelona, España
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Atención presencial previa cita.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="rounded-xl border border-border bg-background p-6 md:p-8 text-center">
        <h2 className="text-xl font-semibold">¿Tienes un local que quieres publicar?</h2>
        <p className="mt-2 text-muted-foreground text-sm max-w-lg mx-auto">
          Publica tu anuncio gratis en Bizaclia y llega a miles de emprendedores buscando locales comerciales.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/mybizaclia"
            className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Publicar anuncio
          </Link>
          <Link
            to="/portal/$" params={{ _splat: "alquiler" }} search={{} as never}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Explorar locales
          </Link>
        </div>
      </section>
    </main>
  );
}
